import { useEffect, useRef } from 'react';
import useDragPreviewManagerValue from 'layout-dnd-utils/DragPreview/useDragPreviewManagerValue';
import { useDragPreviewManager } from 'layout-dnd-utils/DragPreview/DragPreviewManager';
import { clampDragPreviewPosition } from 'layout-dnd-utils/DragPreview/clampDragPreviewPosition';
import { useTransitionStyles } from 'layout-dnd-utils/TransitionStyles/TransitionStylesContext';
import { dropTypes } from 'layout-dnd-utils/constants/dnd';
/**
 * When center-line coercion grows an element's columnsSpanned, its original
 * columnStart may place the span past the right edge of the pegboard, which
 * would cause CSS Grid to add implicit columns and stretch the pegboard.
 * This clamps the column start so the coerced span fits within the pegboard.
 */
const clampCoercedColumnStart = ({
  originalColumnStart,
  coercedColumnsSpanned,
  pegboardColumns
}) => {
  const maxStartColumn = pegboardColumns - coercedColumnsSpanned + 1;
  return Math.min(originalColumnStart, maxStartColumn);
};
const useScrollPosition = () => {
  const scrollPositionRef = useRef({
    initialLeft: window.scrollX,
    initialTop: window.scrollY,
    adjustedLeft: 0,
    adjustedTop: 0,
    currentLeft: window.scrollX,
    currentTop: window.scrollY
  });
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current.adjustedLeft = window.scrollX - scrollPositionRef.current.initialLeft;
      scrollPositionRef.current.adjustedTop = window.scrollY - scrollPositionRef.current.initialTop;
      scrollPositionRef.current.currentLeft = window.scrollX;
      scrollPositionRef.current.currentTop = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return scrollPositionRef.current;
};
/**
 * Renders a shadowed drag preview of the element to be dragged, with the same
 * size and position as the dragged element. The dragged element is updated to
 * then follow the cursor position when the cursor is moved.
 */
const DragPreview = ({
  scale = 1,
  element,
  sourceClientOffset,
  differenceFromInitialOffset,
  pegboardBounds,
  currentTranslationCoords,
  dropTarget,
  gridBoundingBox,
  clampWidth
}) => {
  const dragPreviewManager = useDragPreviewManager();
  const transitionStylesManager = useTransitionStyles();
  const zIndex = useDragPreviewManagerValue(_dragPreviewManager => _dragPreviewManager.getDragPreviewZIndex());
  const {
    adjustedLeft,
    adjustedTop,
    currentLeft,
    currentTop
  } = useScrollPosition();
  const originalWidthRef = useRef(null);
  const dragPreviewDimensions = useRef(null);
  useEffect(() => {
    return () => {
      dragPreviewManager.setDragPreviewPosition({
        elementWidth: 0,
        elementHeight: 0,
        elementLeft: 0,
        elementTop: 0
      });
    };
  }, [dragPreviewManager]);
  useEffect(() => {
    if (element) {
      transitionStylesManager.registerTransitionStyle(element, 'transform');
      transitionStylesManager.registerTransitionStyle(element, 'z-index');
      transitionStylesManager.registerTransitionStyle(element, 'grid-column');
    }
  }, [element, transitionStylesManager]);

  // Updates the dragged element to follow the cursor position when the cursor
  // is moved. It's adjusted to account for a cursor offset and scroll position.
  useEffect(() => {
    if (element && differenceFromInitialOffset && sourceClientOffset) {
      var _originalWidthRef$cur;
      let deltaX = 0;
      let deltaY = 0;
      if (!dragPreviewDimensions.current) {
        dragPreviewDimensions.current = element.getBoundingClientRect();
      }
      const {
        height: rectHeight,
        width: rectWidth
      } = dragPreviewDimensions.current;
      if ((dropTarget === null || dropTarget === void 0 ? void 0 : dropTarget.type) === dropTypes.CENTER_ON_PEGBOARD && pegboardBounds) {
        if (originalWidthRef.current == null) {
          originalWidthRef.current = rectWidth;
        }
        const previewStartColumn = clampCoercedColumnStart({
          originalColumnStart: gridBoundingBox.columnLeft,
          coercedColumnsSpanned: dropTarget.columnsSpanned,
          pegboardColumns: pegboardBounds.numColumns
        });
        element.style.gridColumn = `${previewStartColumn} / span ${dropTarget.columnsSpanned}`;
      } else if (originalWidthRef.current != null) {
        originalWidthRef.current = null;
        element.style.removeProperty('grid-column');
      }
      const effectiveWidth = (_originalWidthRef$cur = originalWidthRef.current) !== null && _originalWidthRef$cur !== void 0 ? _originalWidthRef$cur : rectWidth;
      if (pegboardBounds) {
        ({
          deltaX,
          deltaY
        } = clampDragPreviewPosition({
          sourceX: sourceClientOffset.x,
          sourceY: sourceClientOffset.y,
          elementWidth: clampWidth !== null && clampWidth !== void 0 ? clampWidth : effectiveWidth,
          bounds: pegboardBounds
        }));
      }
      let panDeltaX = 0;
      let panDeltaY = 0;
      if (currentTranslationCoords) {
        const startCoords = dragPreviewManager.getDragStartTranslationCoords();
        panDeltaX = currentTranslationCoords.translateX - startCoords.translateX;
        panDeltaY = currentTranslationCoords.translateY - startCoords.translateY;
      }
      const x = (differenceFromInitialOffset.x + adjustedLeft + deltaX - panDeltaX) / scale;
      const y = (differenceFromInitialOffset.y + adjustedTop + deltaY - panDeltaY) / scale;
      dragPreviewManager.setDragPreviewPosition({
        elementWidth: clampWidth !== null && clampWidth !== void 0 ? clampWidth : effectiveWidth,
        elementHeight: rectHeight,
        elementLeft: sourceClientOffset.x + deltaX + currentLeft,
        elementTop: sourceClientOffset.y + deltaY + currentTop
      });
      element.style.transform = `translate(${x}px, ${y}px)`;
      element.style.zIndex = zIndex;
    }
  }, [element, adjustedLeft, adjustedTop, currentLeft, currentTop, zIndex, scale, differenceFromInitialOffset, sourceClientOffset, dragPreviewManager, pegboardBounds, currentTranslationCoords, dropTarget, gridBoundingBox, clampWidth]);
  return null;
};
export default DragPreview;